import { ImageField, ImageInput, ImageInputProps } from 'react-admin';
import { id } from '../utils';
import { ImageFormData, isImageFormData } from '../DataProvider/utils';
import { match, P as Is } from 'ts-pattern';

export const MAX_IMAGE_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Adapts a simple string formatter so this input can work with the object shape
 * expected by `ImageInput`.
 *
 * The formatter returned from this helper is responsible for turning persisted
 * image values, such as a stored URL, into the preview-friendly `ImageFormData`
 * structure consumed by the field. If a plain string is provided, that value is
 * first delegated to `userFormatFn`, allowing caller-defined formatting to run
 * before the image metadata is constructed.
 *
 * Values that are already in `ImageFormData` form are preserved as-is so
 * existing image state is not rebuilt unnecessarily, while missing or
 * unrecognized values are treated as the absence of an image.
 */
function format(userFormatFn: (value: string) => string = id) {
  return (value: string | ImageFormData | null): ImageFormData | null =>
    match(value)
      .with(Is.string, (str) => {
        const val = userFormatFn(str);
        return { src: val, title: val } as ImageFormData;
      })
      .when(isImageFormData, (data) => data)
      .otherwise(() => null);
}

/**
 * An image uploader component which will use a string field populated with a
 * URL as its source.
 *
 * This component currently allows uploading the following image types:
 *   - AVIF
 *   - GIF
 *   - HEIC
 *   - JPEG/JPEG-XL
 *   - PNG
 *   - SVG
 *   - WEBP
 */
export default function ImageUrlInput(props: Omit<ImageInputProps, 'children'>) {
  return (
    <ImageInput
      accept={{
        'image/avif': ['.avif'],
        'image/gif': ['.gif'],
        'image/heic': ['.heic'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/jxl': ['.jxl'],
        'image/png': ['.png'],
        'image/svg+xml': ['.svg'],
        'image/webp': ['.webp'],
      }}
      maxSize={MAX_IMAGE_UPLOAD_SIZE_BYTES}
      {...props}
      format={format(props.format)}
    >
      <ImageField source="src" />
    </ImageInput>
  );
}
